import { Injectable } from '@angular/core';
import { User } from 'app/user.interface';
import { Subject } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { CustomerProjectType } from '../../customer-project-type.enum';
import { CustomerProject, ProjectAccess } from '../../customer-project.interface';
import { ApiService } from '../api/api.service';

@Injectable({
    providedIn: 'root',
})
export class CustomerProjectService {
    private readonly projectsChanged$ = new Subject<void>();

    constructor(private apiService: ApiService) {}

    getProjectConsumers(projectId: number) {
        return this.apiService.getCall$<User[]>(`customer-projects/consumers/${projectId}`);
    }

    getProjectnames() {
        return this.apiService.getCall$<
            {
                id: number;
                name: string;
            }[]
        >(`customer-projects/names`);
    }

    getConsumerProjects() {
        return this.apiService.getCall$<
            {
                id: number;
                name: string;
                customer_id: number;
                customer_name: string;
            }[]
        >(`customerprojects-for-consumers`);
    }

    follow(customerProjectId: number) {
        return this.apiService.postCall$<boolean>(`customer-projects/follow`, { customerProjectId });
    }

    unfollow(customerProjectId: number) {
        return this.apiService.postCall$<boolean>(`customer-projects/unfollow`, { customerProjectId });
    }

    getProjects(
        onlyForConsumers = false,
        customerId?: number,
        projectType?: CustomerProjectType[],
        archive?: boolean,
        withContacts?: boolean
    ) {
        return this.projectsChanged$.pipe(
            startWith(undefined),
            switchMap(() =>
                this.apiService.getCallData$<CustomerProject[]>('customer-projects', {
                    onlyForConsumers,
                    customerId: customerId ?? '',
                    projectType: projectType?.length ? projectType : '',
                    archive: archive ?? '',
                    withContacts: withContacts ?? '',
                })
            )
        );
    }

    getProjectTemplates(type?: CustomerProjectType) {
        return this.apiService.getCall$<CustomerProject[]>('customer-projects/templates', { type: type || '' });
    }

    getProjectEditUsers(customerId: number) {
        return this.apiService.getCallData$<{ managers: User[]; customers: User[] }>(
            `customer-projects/edit-users/${customerId}`
        );
    }

    getProjectsWorkareaCounts() {
        return this.apiService
            .getCall$<
                Record<
                    number,
                    {
                        name: string;
                        count: number;
                        colorcode: string;
                    }[]
                >
            >('customer-projects/counts')
            .pipe(map(c => c.data));
    }

    getPendingAccessCounts(customerId?: number) {
        return this.apiService
            .getCall$<{ total: number; per_project: Record<number, number> }>(
                'customer-projects/pending-access-counts',
                { customerId: customerId ?? '' }
            )
            .pipe(map(c => c.data));
    }

    getProjectsWorkareaCount(customerProjectId: number) {
        return this.apiService
            .getCall$<
                {
                    name: string;
                    count: number;
                    colorcode: string;
                    id: number;
                }[]
            >(`customer-projects/count/${customerProjectId}`)
            .pipe(map(c => c.data));
    }

    /**
     * The minimal, safe fields (id, type, name, ...) available for any project regardless of
     * customer_project_access - what workarea.component and other lightweight lookups need.
     */
    getProject(id: number) {
        return this.apiService.getCall$<CustomerProject>(`customer-projects/${id}`);
    }

    /**
     * The full project, including phases/budget/extra - gated behind customer_project_access.
     */
    getProjectDetail(id: number) {
        return this.apiService.getCall$<CustomerProject>(`customer-projects/${id}/detail`);
    }

    getProjectAccess(id: number) {
        return this.apiService.getCallData$<ProjectAccess>(`customer-projects/${id}/access`);
    }

    requestProjectAccess(id: number) {
        return this.apiService.postCall$(`customer-projects/${id}/access/request`, {});
    }

    grantProjectAccess(id: number, userId: number) {
        return this.apiService.postCall$(`customer-projects/${id}/access/grant`, { userId });
    }

    revokeProjectAccess(id: number, userId: number) {
        return this.apiService.deleteCall$(`customer-projects/${id}/access/${userId}`);
    }

    setAccessEmailNotifications(id: number, userId: number, emailNotifications: boolean) {
        return this.apiService.postCall$(`customer-projects/${id}/access/${userId}/notifications`, {
            email_notifications: emailNotifications,
        });
    }

    saveProject(project: Partial<CustomerProject> & { current_customer_id?: number }) {
        return this.apiService
            .postCall$<CustomerProject>('customer-projects', project)
            .pipe(tap(() => this.projectsChanged$.next()));
    }

    deleteProject(id: number) {
        return this.apiService.deleteCall$(`customer-projects/${id}`).pipe(tap(() => this.projectsChanged$.next()));
    }

    public zip(id: number) {
        return this.apiService.getBlobCall(`customer-projects/download/${id}`);
    }
}
